// Analytics and visitor tracking service
import { API_BASE_URL } from './api';

// Generate a unique session ID for this browser session
const generateSessionId = (): string => {
  let sessionId = sessionStorage.getItem('visitor_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('visitor_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const userAgent = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect browser
const getBrowser = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf('Chrome') > -1) return 'Chrome';
  if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
  if (userAgent.indexOf('Safari') > -1) return 'Safari';
  if (userAgent.indexOf('Edge') > -1) return 'Edge';
  if (userAgent.indexOf('Opera') > -1) return 'Opera';
  return 'Other';
};

// Detect OS
const getOS = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf('Windows') > -1) return 'Windows';
  if (userAgent.indexOf('Mac') > -1) return 'macOS';
  if (userAgent.indexOf('Linux') > -1) return 'Linux';
  if (userAgent.indexOf('Android') > -1) return 'Android';
  if (userAgent.indexOf('iOS') > -1) return 'iOS';
  return 'Other';
};

// Get page type based on URL
type PageType = 'home' | 'product' | 'category' | 'collection' | 'checkout' | 'contact' | 'marketing' | 'other';

const getPageType = (url: string): PageType => {
  const path = new URL(url).pathname;
  if (path === '/' || path === '/hem') return 'home';
  if (path.startsWith('/produkt/') || path.startsWith('/produkter/')) return 'product';
  if (path === '/produkter' || path.startsWith('/produkter')) return 'category';
  if (path.startsWith('/kollektion/')) return 'collection';
  if (path === '/kassa' || path === '/checkout') return 'checkout';
  if (path === '/kontakt') return 'contact';
  return 'other';
};

// Extract product ID from URL
const getProductIdFromUrl = (url: string): number | null => {
  const match = url.match(/\/(?:produkt|produkter)\/([^\/]+)/);
  if (match) {
    // You might need to map slug to ID, or include ID in URL
    // For now, return null - this would need product lookup
    return null;
  }
  return null;
};

class AnalyticsService {
  private sessionId: string;
  private pageStartTime: number = Date.now();
  private isTrackingEnabled: boolean = true;

  constructor() {
    this.sessionId = generateSessionId();
    this.initializeSession();
    this.setupPageTracking();
  }

  // Initialize visitor session
  private async initializeSession() {
    if (!this.isTrackingEnabled) return;

    try {
      const sessionData = {
        session_id: this.sessionId,
        ip_address: null, // Will be set by server
        user_agent: navigator.userAgent,
        referrer_url: document.referrer || 'direct',
        landing_page: window.location.pathname,
        device_type: getDeviceType(),
        browser: getBrowser(),
        os: getOS()
      };

      await fetch(`${API_BASE_URL}/visitor/track-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sessionData)
      });
    } catch (error) {
      console.warn('Analytics: Failed to initialize session', error);
    }
  }

  // Track page view
  public async trackPageView(title?: string, productId?: number, categoryId?: number, pageTypeOverride?: PageType) {
    if (!this.isTrackingEnabled) return;

    try {
      const datasetType = (document?.body?.dataset?.pageType as PageType | undefined) || undefined;
      const pageData = {
        session_id: this.sessionId,
        page_url: window.location.href,
        page_title: title || document.title,
        page_type: pageTypeOverride || datasetType || getPageType(window.location.href),
        product_id: productId || getProductIdFromUrl(window.location.href),
        category_id: categoryId,
        time_on_page: Math.round((Date.now() - this.pageStartTime) / 1000)
      };

      await fetch(`${API_BASE_URL}/visitor/track-page`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pageData)
      });

      // Reset page start time for next page
      this.pageStartTime = Date.now();
    } catch (error) {
      console.warn('Analytics: Failed to track page view', error);
    }
  }

  // Send GA4/gtag-compatible events when available
  private sendGAEvent(eventName: string, params: Record<string, any>) {
    try {
      const w = window as any;
      if (typeof w.gtag === 'function') {
        w.gtag('event', eventName, params);
      } else if (Array.isArray(w.dataLayer)) {
        w.dataLayer.push({ event: eventName, ...params });
      }
    } catch (error) {
      console.warn('Analytics: Failed to send GA4 event', error);
    }
  }

  // Track custom event
  public async trackEvent(
    eventType: 'click' | 'scroll' | 'add_to_cart' | 'remove_from_cart' | 'purchase' | 'form_submit',
    eventData: any = {},
    productId?: number
  ) {
    if (!this.isTrackingEnabled) return;

    try {
      const event = {
        session_id: this.sessionId,
        event_type: eventType,
        event_data: eventData,
        page_url: window.location.href,
        product_id: productId
      };

      await fetch(`${API_BASE_URL}/visitor/track-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(event)
      });
    } catch (error) {
      console.warn('Analytics: Failed to track event', error);
    }
  }

  // Set up automatic page tracking
  private setupPageTracking() {
    // Track initial page load
    this.trackPageView();

    // Track page changes for SPA
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (...args) => {
      originalPushState.apply(window.history, args);
      setTimeout(() => this.trackPageView(), 100);
    };

    window.history.replaceState = (...args) => {
      originalReplaceState.apply(window.history, args);
      setTimeout(() => this.trackPageView(), 100);
    };

    // Listen for popstate (back/forward)
    window.addEventListener('popstate', () => {
      setTimeout(() => this.trackPageView(), 100);
    });

    // Track page unload (time on page)
    window.addEventListener('beforeunload', () => {
      this.trackPageView(); // Final page view with time spent
    });
  }

  // Convenience methods for common events
  public trackProductView(productId: number, productName: string) {
    this.trackEvent('click', { 
      action: 'product_view', 
      product_name: productName 
    }, productId);

    // GA4 view_item event
    this.sendGAEvent('view_item', {
      items: [
        {
          item_id: String(productId),
          item_name: productName
        }
      ]
    });
  }

  public trackAddToCart(productId: number, productName: string, quantity: number, price: number) {
    this.trackEvent('add_to_cart', {
      product_name: productName,
      quantity,
      price,
      total: quantity * price
    }, productId);

    // GA4 add_to_cart event
    this.sendGAEvent('add_to_cart', {
      currency: 'SEK',
      value: quantity * price,
      items: [
        {
          item_id: String(productId),
          item_name: productName,
          quantity,
          price
        }
      ]
    });
  }

  public trackRemoveFromCart(productId: number, productName: string, quantity: number) {
    this.trackEvent('remove_from_cart', {
      product_name: productName,
      quantity
    }, productId);
  }

  // GA4 begin_checkout event based on the secure order payload from checkout
  public trackBeginCheckout(orderData: {
    order_number: string;
    total_amount: number;
    items: Array<{ product_id: number; product_name: string; quantity: number; unit_price: number; }>;
    campaign_code?: string | null;
  }) {
    this.sendGAEvent('begin_checkout', {
      currency: 'SEK',
      value: orderData.total_amount,
      coupon: orderData.campaign_code || undefined,
      items: orderData.items.map(item => ({
        item_id: String(item.product_id),
        item_name: item.product_name,
        quantity: item.quantity,
        price: item.unit_price
      }))
    });
  }

  public trackPurchase(orderData: {
    order_number: string;
    total_amount: number;
    shipping_cost?: number;
    tax_amount?: number;
    campaign_code?: string | null;
    items: Array<{ product_id: number; quantity: number; price: number; name?: string }>;
  }) {
    this.trackEvent('purchase', {
      order_number: orderData.order_number,
      total_amount: orderData.total_amount,
      items_count: orderData.items.length,
      items: orderData.items
    });

    // GA4 purchase event
    this.sendGAEvent('purchase', {
      transaction_id: orderData.order_number,
      currency: 'SEK',
      value: orderData.total_amount,
      shipping: orderData.shipping_cost ?? 0,
      tax: orderData.tax_amount ?? 0,
      coupon: orderData.campaign_code || undefined,
      items: orderData.items.map(item => ({
        item_id: String(item.product_id),
        item_name: item.name,
        quantity: item.quantity,
        price: item.price
      }))
    });
  }

  public trackFormSubmit(formType: string, formData: any = {}) {
    this.trackEvent('form_submit', {
      form_type: formType,
      ...formData
    });
  }

  // Enable/disable tracking (for privacy)
  public setTrackingEnabled(enabled: boolean) {
    this.isTrackingEnabled = enabled;
    if (enabled) {
      localStorage.setItem('analytics_enabled', 'true');
    } else {
      localStorage.removeItem('analytics_enabled');
      sessionStorage.removeItem('visitor_session_id');
    }
  }

  public isTrackingAllowed(): boolean {
    return this.isTrackingEnabled && localStorage.getItem('analytics_enabled') !== 'false';
  }
}

// Create global analytics instance
const analytics = new AnalyticsService();

export default analytics;

// Export types for admin dashboard
export interface VisitorStatistics {
  period_days: number;
  statistics: {
    overview: {
      total_visitors: number;
      total_sessions: number;
      avg_pages_per_session: number;
      avg_session_duration: number;
    };
    daily_views: Array<{
      date: string;
      views: number;
    }>;
  };
  top_products: Array<{
    id: number;
    name: string;
    slug: string;
    price: number;
    view_count: number;
    unique_visitors: number;
  }>;
  top_pages: Array<{
    page_url: string;
    page_title: string;
    page_type: string;
    view_count: number;
    unique_visitors: number;
    avg_time_on_page: number;
  }>;
  device_stats: {
    devices: Array<{
      device_type: string;
      count: number;
      percentage: number;
    }>;
    browsers: Array<{
      browser: string;
      count: number;
      percentage: number;
    }>;
  };
  referrer_stats: Array<{
    referrer_source: string;
    count: number;
    percentage: number;
  }>;
  conversion_stats: Array<{
    event_type: string;
    count: number;
    unique_sessions: number;
  }>;
}
